import type { Ref } from 'vue'

import { onMounted, readonly, ref } from 'vue'

/** How long to wait at most. Long enough for the first screen to settle, short enough that a sheet is ready. */
const IDLE_TIMEOUT_MS = 2000

/**
 * True once the page has painted and the browser has a quiet moment.
 *
 * What a screen only needs later — its sheets, the scanner, the sign-in and the
 * phone parser inside it — renders from here, so the first screen downloads and
 * runs only what it shows. Each of those also renders the moment it is opened,
 * so nothing waits on this; it only decides how early they load.
 *
 * Safari has no requestIdleCallback, so it gets a fixed beat instead.
 */
export const useAfterIdle = (): Readonly<Ref<boolean>> => {
  const isIdle = ref(false)

  onMounted(() => {
    const settle = (): void => {
      isIdle.value = true
    }

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(settle, { timeout: IDLE_TIMEOUT_MS })
    } else {
      setTimeout(settle, IDLE_TIMEOUT_MS / 2)
    }
  })

  return readonly(isIdle)
}
